import { create } from 'zustand';
import ServiceCBO from '@/services/ServiceCBO';
import { notify } from '@/stores/useToast';
import { MSG_ERRO_EXPORTACAO } from '@/constants/ConstantSuporte';
import {
  buildRequestBody,
  buildStepVM,
  buildStatusVM,
  buildClassificationVM,
  buildRiskVM,
  CLASSIFICATION_LABEL,
  RISK_LABEL,
  STATUS_LABEL,
  STEP_LABEL,
  SITUACAO_CONTRATUAL_LABEL,
  PCD_LABEL,
  FILTROS_OPCOES_VAZIO,
  type Barra,
  type CboTableFiltros,
  type CboFiltrosOpcoes,
} from '@/lib/cbo';
import { buildUpdateBody, type CboAnaliseForm } from '@/lib/cboAnalise';
import UtilsDate from '@/utils/UtilsDate';
import UtilsMask from '@/utils/UtilsMask';
import { isPerfilAnalista } from '@/utils/perfil';

// Classificações fixas da aba Risco (paridade com o legado: a tabela de Risco
// sempre injeta este par, independente do filtro do usuário).
const CLASSIF_RISCO = ['RISCO_DETECTADO', 'INEXPRESSIVO'];

// Exige ≥1 letra (paridade com a validação do legado/BFF).
const HAS_LETTER = /[A-Za-zÀ-ÿ]/;

// CON-5188: cada coluna ordenável da tabela mapeia para o alias que o backend aceita
// (history/cbo-analysis?sort=<alias>,<asc|desc>). Só as colunas com alias entram como
// sortable — as demais não têm suporte de ordenação no BFF.
const SORT_ALIASES: Record<string, string> = {
  empresa: 'nomeEmpresa',
  cpf: 'cpf',
  matricula: 'matricula',
  nome: 'nome',
  setor: 'setor',
  cargo: 'cargo',
  etapa: 'etapa',
  classificacao: 'classificacao',
  risco: 'risco',
  ocupacao: 'ocupacao',
};

export interface LinhaCbo {
  [k: string]: unknown;
  _key: string;
  idTrabalhador: number | null;
  empresa: string;
  cnpj: string;
  cpf: string;
  matricula: string;
  nome: string;
  nomeSocial: string;
  setor: string;
  cargo: string;
  etapa: string;
  status: string;
  classificacao: string;
  risco: string;
  ocupacao: string;
  ocupacaoRecomendada: string;
  diagnostico: string;
  descricaoAtividades: string;
  descricaoAtividadesRecomendada: string;
  descricaoSumaria: string;
  descricaoSumariaRecomendada: string;
  grauInstrucao: string;
  escolaridadeMinima: string;
  orgaoClasse: string;
  cotaAprendizagem: string;
  ambienteS2240: string;
  ambienteS2200S2206: string;
  inicioS2240: string;
  fimS2240: string;
  versao: string;
  regimePrevidenciario: string;
  // ---- CON-4346 ----
  situacaoContratual: string;
  inicioContrato: string;
  fimContrato: string;
  pcd: string;
  categoria: string;
  idEventoDadosContratuais: number | null;
}

export interface LinhaPesquisa {
  [k: string]: unknown;
  idColumn: number;
  compatibility: string;
  occupation: string;
  education: string;
  professionalRegulatoryBody: string;
}

const FILTROS_VAZIO: CboTableFiltros = {
  step: [],
  status: [],
  classification: [],
  risk: [],
  analysisOccupationCode: [],
  analysisOccurrenceId: [],
  occupationCode: [],
  activityDescription: [],
  activityDescriptionRecommended: [],
  cargo: [],
  setor: [],
  nociveAgentEnvironment: [],
  legalAgreementEnvironment: [],
  regimeTrabalhista: [],
  grauInstrucao: [],
  escolaridadeMinima: [],
  cotaAprendizagem: null,
  orgaoClasse: [],
  pcd: null,
  categoria: [],
};

type Any = any;

const normalizar = (h: Any, i: number): LinhaCbo => ({
  _key: `${h.idTrabalhador ?? 'row'}-${i}`,
  idTrabalhador: h.idTrabalhador ?? null,
  empresa: h.nomeEmpresa ?? '',
  cnpj: h.cnpj ? UtilsMask.cnpj(h.cnpj) : '',
  cpf: h.cpf ? UtilsMask.cpf(h.cpf) : '',
  matricula: h.matricula ?? '',
  nome: h.nomeSocial ? `${h.nome ?? ''} / ${h.nomeSocial}` : (h.nome ?? ''),
  nomeSocial: h.nomeSocial ?? '',
  setor: h.setor ?? '',
  cargo: h.cargo ?? '',
  etapa: STEP_LABEL[h.etapa as string] ?? h.etapa ?? '',
  status: STATUS_LABEL[h.status as string] ?? h.status ?? '',
  classificacao: CLASSIFICATION_LABEL[h.classificacao as string] ?? h.classificacao ?? '',
  risco: RISK_LABEL[h.risco as string] ?? h.risco ?? '',
  ocupacao: h.ocupacao ?? '',
  ocupacaoRecomendada: h.ocupacaoRecomendada ?? '',
  diagnostico: h.diagnostico ?? '',
  descricaoAtividades: h.descricaoAtividades ?? '',
  descricaoAtividadesRecomendada: h.descricaoAtividadesRecomendada ?? '',
  descricaoSumaria: h.descricaoSumaria ?? '',
  descricaoSumariaRecomendada: h.descricaoSumariaRecomendada ?? '',
  grauInstrucao: h.grauInstrucao ?? '',
  escolaridadeMinima: h.escolaridadeMinima ?? '',
  orgaoClasse: h.orgaoClasse ?? '',
  cotaAprendizagem:
    h.cotaAprendizagem == null
      ? ''
      : (PCD_LABEL[String(h.cotaAprendizagem)] ?? String(h.cotaAprendizagem)),
  ambienteS2240: h.ambienteS2240 ?? '',
  ambienteS2200S2206: h.ambienteS2200S2206 ?? '',
  inicioS2240: UtilsDate.format2(h.inicioS2240),
  fimS2240: UtilsDate.format2(h.fimS2240),
  versao: h.versao ?? '',
  regimePrevidenciario: h.regimePrevidenciario ?? '',
  situacaoContratual:
    SITUACAO_CONTRATUAL_LABEL[(h.situacaoContratual as string)?.toUpperCase()] ??
    h.situacaoContratual ??
    '',
  inicioContrato: UtilsDate.format2(h.inicioContrato),
  fimContrato: UtilsDate.format2(h.fimContrato),
  pcd: h.pcd == null ? '' : (PCD_LABEL[String(h.pcd)] ?? String(h.pcd)),
  categoria: h.categoria ?? '',
  idEventoDadosContratuais: h.idEventoDadosContratuais ?? null,
});

interface State {
  empregador: string;
  filtros: CboTableFiltros;
  opcoesFiltro: CboFiltrosOpcoes;
  busca: string;
  modoRisco: boolean;
  stepVM: Barra[];
  statusVM: Barra[];
  classificationVM: Barra[];
  riskVM: Barra[];
  riskTotal: number;
  lista: LinhaCbo[];
  total: number;
  page: number;
  size: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  carregandoGraficos: boolean;
  carregandoLista: boolean;
  exportando: boolean;
  exportandoClientView: boolean;
  exportandoPesquisa: boolean;
  // aba Pesquisa
  searchTerm: string;
  searchResults: LinhaPesquisa[];
  searchTotal: number;
  searchPage: number;
  searchSize: number;
  carregandoBusca: boolean;
  // análise do analista (Fase 2)
  analise: Any | null;
  analiseTrabalhadorId: number | null;
  carregandoAnalise: boolean;
  historico: Any[];
  historicoTotal: number;

  // informações adicionais da análise (CON-4206) — texto + anexo opcional, só analista
  informacoesAdicionais: Any[];
  carregandoInformacoesAdicionais: boolean;
  historicoInformacoesAdicionais: Any[];

  carregarVisaoGeral: (analista?: boolean) => Promise<void>;
  carregarRisco: () => Promise<void>;
  carregarHistorico: () => Promise<void>;
  carregarFiltros: () => Promise<void>;
  aplicarEmpregador: (cnpj: string) => Promise<void>;
  setFiltros: (f: CboTableFiltros) => Promise<void>;
  limparFiltros: () => Promise<void>;
  setBusca: (v: string) => void;
  buscar: () => Promise<void>;
  setPage: (page: number) => Promise<void>;
  setSize: (size: number) => Promise<void>;
  setSort: (by: string, order: 'asc' | 'desc') => Promise<void>;
  pesquisar: (term: string) => Promise<void>;
  limparPesquisa: () => void; // CON-5352: reseta a aba Pesquisa ao sair dela
  carregarAnalise: (trabalhadorId: number) => Promise<void>;
  // CON-5383: sugere a ocupação a partir da descrição das atividades do cliente
  // (reusa a API de recomendação collaborator/cbo) quando não há recomendação
  // explícita no getAnalysis. Retorna o topo só quando compatibilidade total.
  sugerirOcupacaoPorDescricao: (
    descricao: string,
  ) => Promise<{ code: string; title: string } | null>;
  salvarAnalise: (form: CboAnaliseForm) => Promise<void>;
  carregarHistoricoAnalise: () => Promise<void>;
  carregarInformacoesAdicionais: () => Promise<void>;
  adicionarInformacaoAdicional: (texto: string, file: File | null) => Promise<void>;
  editarInformacaoAdicional: (
    id: number,
    texto: string,
    justificativa: string,
    file: File | null,
    removerAnexo: boolean,
  ) => Promise<void>;
  excluirInformacaoAdicional: (id: number) => Promise<void>;
  carregarHistoricoInformacoesAdicionais: () => Promise<void>;
  exportar: () => Promise<void>;
  exportarClientView: () => Promise<void>;
  exportarPesquisa: () => Promise<void>;
}

// Corpo compartilhado por tabela e gráficos (CON-5224): filtro geral (empregador) +
// filtros da tabela + busca. O BFF ignora o filterTerm nos gráficos — só a listagem o aplica.
function _body(get: () => State) {
  const s = get();
  const body = buildRequestBody({
    cnpjList: s.empregador ? [s.empregador] : [],
    busca: s.busca,
    filtros: s.filtros,
  });
  // Aba Risco: força o par de classificação (paridade com o legado), sobrepondo
  // o filtro de classificação da tabela.
  if (s.modoRisco) body.classification = [...CLASSIF_RISCO];
  return body;
}

export const useCboStore = create<State>((set, get) => ({
  empregador: '',
  filtros: { ...FILTROS_VAZIO },
  opcoesFiltro: { ...FILTROS_OPCOES_VAZIO },
  busca: '',
  modoRisco: false,
  stepVM: buildStepVM(undefined),
  statusVM: buildStatusVM(undefined),
  classificationVM: buildClassificationVM(undefined),
  riskVM: [],
  riskTotal: 0,
  lista: [],
  total: 0,
  page: 0,
  size: 50,
  sortBy: '',
  sortOrder: 'asc',
  carregandoGraficos: false,
  carregandoLista: false,
  exportando: false,
  exportandoClientView: false,
  exportandoPesquisa: false,
  searchTerm: '',
  searchResults: [],
  searchTotal: 0,
  searchPage: 0,
  searchSize: 500,
  carregandoBusca: false,
  analise: null,
  analiseTrabalhadorId: null,
  carregandoAnalise: false,
  historico: [],
  historicoTotal: 0,
  informacoesAdicionais: [],
  carregandoInformacoesAdicionais: false,
  historicoInformacoesAdicionais: [],

  // Perfil analista → gráfico de Etapa (step); perfil cliente → gráfico de Status.
  // Classificação sempre. Paridade com o legado (isPerfilAnalista).
  async carregarVisaoGeral(analista = true) {
    set({ modoRisco: false, carregandoGraficos: true });
    try {
      const [etapaOuStatus, classification] = await Promise.all([
        analista ? ServiceCBO.dashboardStep(_body(get)) : ServiceCBO.dashboardStatus(_body(get)),
        ServiceCBO.dashboardClassification(_body(get)),
      ]);
      set(
        analista
          ? {
              stepVM: buildStepVM(etapaOuStatus.data),
              classificationVM: buildClassificationVM(classification.data),
            }
          : {
              statusVM: buildStatusVM(etapaOuStatus.data),
              classificationVM: buildClassificationVM(classification.data),
            },
      );
    } catch {
      set({
        stepVM: buildStepVM(undefined),
        statusVM: buildStatusVM(undefined),
        classificationVM: buildClassificationVM(undefined),
      });
      notify.error('Erro ao carregar os gráficos de análise CBO.');
    } finally {
      set({ carregandoGraficos: false });
    }
    await get().carregarHistorico();
  },

  async carregarRisco() {
    set({ modoRisco: true, carregandoGraficos: true });
    try {
      const { data } = await ServiceCBO.dashboardRisk(_body(get));
      set({ riskVM: buildRiskVM(data), riskTotal: data?.totalItems ?? 0 });
    } catch {
      set({ riskVM: [], riskTotal: 0 });
      notify.error('Erro ao carregar o gráfico de exposição ao risco.');
    } finally {
      set({ carregandoGraficos: false });
    }
    await get().carregarHistorico();
  },

  async carregarHistorico() {
    const { page, size, sortBy, sortOrder } = get();
    set({ carregandoLista: true });
    try {
      const alias = SORT_ALIASES[sortBy];
      const { data } = await ServiceCBO.history(_body(get), {
        page,
        size,
        sort: alias ? `${alias},${sortOrder}` : undefined,
      });
      set({
        lista: (data.content ?? []).map(normalizar),
        total: data.totalElements ?? 0,
      });
    } catch {
      set({ lista: [], total: 0 });
      notify.error('Erro ao carregar os trabalhadores.');
    } finally {
      set({ carregandoLista: false });
    }
  },

  async carregarFiltros() {
    try {
      const { data } = await ServiceCBO.filters(null);
      set({ opcoesFiltro: { ...FILTROS_OPCOES_VAZIO, ...data } });
    } catch {
      set({ opcoesFiltro: { ...FILTROS_OPCOES_VAZIO } });
      notify.error('Erro ao carregar as opções de filtro.');
    }
  },

  async aplicarEmpregador(cnpj) {
    set({ empregador: cnpj, page: 0 });
  },

  async setFiltros(f) {
    set({ filtros: f, page: 0 });
    // CON-5224: aplicar filtro recalcula os gráficos junto com a listagem (os loaders de
    // gráfico já recarregam a listagem no fim). Preserva a aba atual (Risco x Visão geral).
    await (get().modoRisco ? get().carregarRisco() : get().carregarVisaoGeral(isPerfilAnalista()));
  },

  async limparFiltros() {
    set({ filtros: { ...FILTROS_VAZIO }, page: 0 });
    // CON-5224: limpar filtro também volta os gráficos ao geral (junto com a listagem).
    await (get().modoRisco ? get().carregarRisco() : get().carregarVisaoGeral(isPerfilAnalista()));
  },

  setBusca(v) {
    set({ busca: v });
  },

  async buscar() {
    set({ page: 0 });
    await get().carregarHistorico();
  },

  async setPage(page) {
    set({ page });
    await get().carregarHistorico();
  },

  async setSize(size) {
    set({ size, page: 0 });
    await get().carregarHistorico();
  },

  async setSort(by, order) {
    set({ sortBy: by, sortOrder: order, page: 0 });
    await get().carregarHistorico();
  },

  async pesquisar(term) {
    const t = (term ?? '').trim();
    set({ searchTerm: term, searchPage: 0 });
    if (!t || !HAS_LETTER.test(t)) {
      set({ searchResults: [], searchTotal: 0 });
      return;
    }
    set({ carregandoBusca: true });
    try {
      const { data } = await ServiceCBO.search({ query: t, page: 0, size: get().searchSize });
      set({ searchResults: data.content ?? [], searchTotal: data.totalElements ?? 0 });
    } catch {
      set({ searchResults: [], searchTotal: 0 });
      notify.error('Erro ao pesquisar ocupações.');
    } finally {
      set({ carregandoBusca: false });
    }
  },

  limparPesquisa() {
    set({
      searchTerm: '',
      searchResults: [],
      searchTotal: 0,
      searchPage: 0,
      carregandoBusca: false,
    });
  },

  async carregarAnalise(trabalhadorId) {
    set({
      carregandoAnalise: true,
      analiseTrabalhadorId: trabalhadorId,
      analise: null,
      historico: [],
      historicoTotal: 0,
      informacoesAdicionais: [],
      historicoInformacoesAdicionais: [],
    });
    try {
      const { data } = await ServiceCBO.getAnalysis(trabalhadorId);
      set({ analise: data });
    } catch {
      set({ analise: null });
      notify.error('Erro ao carregar a análise.');
    } finally {
      set({ carregandoAnalise: false });
    }
  },

  async sugerirOcupacaoPorDescricao(descricao) {
    const t = (descricao ?? '').trim();
    // Mesma validação do legado/BFF: só pesquisa com ≥1 letra.
    if (!t || !HAS_LETTER.test(t)) return null;
    try {
      const { data } = await ServiceCBO.search({ query: t, page: 0, size: 1 });
      const topo = (data?.content ?? [])[0] as LinhaPesquisa | undefined;
      // Só sugere quando a compatibilidade é total (evita empurrar palpite fraco).
      if (!topo || topo.compatibility !== '100%') return null;
      // `occupation` vem como "212415 - Analista" (código - título) — sem campo
      // de código separado, então separamos no primeiro " - ".
      const [codigo, ...resto] = String(topo.occupation ?? '').split(' - ');
      const code = (codigo ?? '').trim();
      if (!code) return null;
      return { code, title: resto.join(' - ').trim() };
    } catch {
      // Sugestão é best-effort: falha silenciosa, o usuário segue preenchendo.
      return null;
    }
  },

  async salvarAnalise(form) {
    const id = get().analiseTrabalhadorId;
    if (!id) return;
    try {
      await ServiceCBO.updateAnalysis(id, buildUpdateBody(form));
      notify.success('Análise salva.');
      const { data } = await ServiceCBO.getAnalysis(id);
      set({ analise: data });
      // Recarrega os gráficos da aba atual — preserva o modo Risco (pré-filtro de
      // classificação) e o perfil (Etapa x Status) em vez de forçar a Visão geral.
      const recarregarGraficos = get().modoRisco
        ? get().carregarRisco()
        : get().carregarVisaoGeral(isPerfilAnalista());
      await Promise.all([get().carregarHistoricoAnalise(), recarregarGraficos]);
    } catch (e) {
      notify.error('Erro ao salvar a análise.');
      throw e;
    }
  },

  async carregarHistoricoAnalise() {
    const id = get().analiseTrabalhadorId;
    const aId = get().analise?.id;
    if (!id || !aId) return;
    try {
      const { data } = await ServiceCBO.getHistory(id, aId, { page: 0, size: 50 });
      set({ historico: data.content ?? [], historicoTotal: data.totalElements ?? 0 });
    } catch {
      notify.error('Erro ao carregar o histórico.');
    }
  },

  async carregarInformacoesAdicionais() {
    const id = get().analiseTrabalhadorId;
    const aId = get().analise?.id;
    if (!id || !aId) return;
    set({ carregandoInformacoesAdicionais: true });
    try {
      const { data } = await ServiceCBO.listInformacoesAdicionais(id, aId);
      set({ informacoesAdicionais: data ?? [] });
    } catch {
      set({ informacoesAdicionais: [] });
      notify.error('Erro ao carregar as informações adicionais.');
    } finally {
      set({ carregandoInformacoesAdicionais: false });
    }
  },

  async adicionarInformacaoAdicional(texto, file) {
    const id = get().analiseTrabalhadorId;
    const aId = get().analise?.id;
    if (!id || !aId) return;
    try {
      await ServiceCBO.criarInformacaoAdicional(id, aId, { texto, file });
      notify.success('Informação adicional cadastrada.');
      await Promise.all([
        get().carregarInformacoesAdicionais(),
        get().carregarHistoricoInformacoesAdicionais(),
      ]);
    } catch (e) {
      notify.error('Erro ao cadastrar a informação adicional.');
      throw e;
    }
  },

  async editarInformacaoAdicional(id, texto, justificativa, file, removerAnexo) {
    const trabalhadorId = get().analiseTrabalhadorId;
    const aId = get().analise?.id;
    if (!trabalhadorId || !aId) return;
    try {
      await ServiceCBO.editarInformacaoAdicional(trabalhadorId, aId, id, {
        texto,
        justificativa,
        file,
        removerAnexo,
      });
      notify.success('Informação adicional atualizada.');
      await Promise.all([
        get().carregarInformacoesAdicionais(),
        get().carregarHistoricoInformacoesAdicionais(),
      ]);
    } catch (e) {
      notify.error('Erro ao editar a informação adicional.');
      throw e;
    }
  },

  async excluirInformacaoAdicional(id) {
    const trabalhadorId = get().analiseTrabalhadorId;
    const aId = get().analise?.id;
    if (!trabalhadorId || !aId) return;
    try {
      await ServiceCBO.excluirInformacaoAdicional(trabalhadorId, aId, id);
      notify.success('Informação adicional excluída.');
      await Promise.all([
        get().carregarInformacoesAdicionais(),
        get().carregarHistoricoInformacoesAdicionais(),
      ]);
    } catch (e) {
      notify.error('Erro ao excluir a informação adicional.');
      throw e;
    }
  },

  async carregarHistoricoInformacoesAdicionais() {
    const id = get().analiseTrabalhadorId;
    const aId = get().analise?.id;
    if (!id || !aId) return;
    try {
      const { data } = await ServiceCBO.historicoInformacoesAdicionais(id, aId);
      set({ historicoInformacoesAdicionais: data ?? [] });
    } catch {
      notify.error('Erro ao carregar o histórico de informações adicionais.');
    }
  },

  async exportar() {
    set({ exportando: true });
    try {
      await ServiceCBO.exportHistory(_body(get));
      notify.success('Exportação em processamento. Acompanhe em Arquivos › Exportações.');
    } catch {
      notify.error(MSG_ERRO_EXPORTACAO);
    } finally {
      set({ exportando: false });
    }
  },

  // visão do cliente: mesmo filtro da tabela, subset de colunas
  async exportarClientView() {
    set({ exportandoClientView: true });
    try {
      await ServiceCBO.exportHistoryClientView(_body(get));
      notify.success('Exportação em processamento. Acompanhe em Arquivos › Exportações.');
    } catch {
      notify.error(MSG_ERRO_EXPORTACAO);
    } finally {
      set({ exportandoClientView: false });
    }
  },

  // aba Pesquisa: exporta o resultado do último termo pesquisado
  async exportarPesquisa() {
    const t = (get().searchTerm ?? '').trim();
    if (!t || !HAS_LETTER.test(t)) return;
    set({ exportandoPesquisa: true });
    try {
      await ServiceCBO.exportSearch({ query: t });
      notify.success('Exportação em processamento. Acompanhe em Arquivos › Exportações.');
    } catch {
      notify.error(MSG_ERRO_EXPORTACAO);
    } finally {
      set({ exportandoPesquisa: false });
    }
  },
}));
